import React, { useEffect, useState } from "react";
import { X, ExternalLink, FileText, Loader2, Download } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InvoiceDrawer = ({ customer, onClose, token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API}/admin/customer/${customer.id}/invoices`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!cancelled) setInvoices(res.data.invoices || []);
      } catch (err) {
        if (!cancelled) setError("Could not load invoices.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customer, token]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric", month: "short", year: "numeric",
    });
  };

  const formatMoney = (amount, currency) => `${(amount || 0).toFixed(2)} ${currency}`;

  const statusStyle = (status) => {
    const map = {
      paid: "bg-[#16C79A]/15 text-[#16C79A]",
      open: "bg-amber-100 text-amber-700",
      draft: "bg-slate-100 text-slate-500",
      uncollectible: "bg-red-50 text-red-500",
      void: "bg-slate-100 text-slate-400",
    };
    return map[status] || "bg-slate-100 text-slate-500";
  };

  if (!customer) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end"
      data-testid="invoice-drawer"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        data-testid="invoice-drawer-backdrop"
      />
      <div className="relative w-full max-w-md h-full bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        <div className="flex items-start justify-between p-6 border-b border-[#e0f5ef]">
          <div>
            <p className="text-xs uppercase tracking-wider text-slate-400 font-bold mb-1">
              Invoice History
            </p>
            <h3 className="text-xl font-bold text-[#0C2D48]">{customer.name}</h3>
            <p className="text-sm text-slate-500 mt-1">{customer.email}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full hover:bg-[#f0faf7] flex items-center justify-center text-slate-500 hover:text-[#0C2D48] transition-colors"
            data-testid="invoice-drawer-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 size={28} className="animate-spin text-[#16C79A] mb-3" />
              <p className="text-sm text-slate-500">Loading invoices…</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-500 text-sm">{error}</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-[#e0f5ef] flex items-center justify-center mx-auto mb-4">
                <FileText size={24} className="text-[#16C79A]" />
              </div>
              <p className="text-sm text-slate-500">
                No invoices yet for this customer.
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Invoices appear here once the customer completes a billing cycle.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  data-testid={`invoice-item-${inv.id}`}
                  className="border border-[#e0f5ef] rounded-xl p-4 hover:border-[#16C79A]/40 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-sm font-semibold text-[#0C2D48]">
                        {inv.number || inv.id.slice(0, 14)}
                      </p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {formatDate(inv.created)}
                      </p>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusStyle(inv.status)}`}
                    >
                      {inv.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="text-lg font-bold text-[#0C2D48]">
                        {formatMoney(inv.amount_paid || inv.amount_due, inv.currency)}
                      </p>
                      {inv.period_start && inv.period_end && (
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          {formatDate(inv.period_start)} → {formatDate(inv.period_end)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 rounded-lg bg-[#f0faf7] hover:bg-[#16C79A]/15 flex items-center justify-center text-[#16C79A] transition-colors"
                          title="View invoice"
                          data-testid={`invoice-view-${inv.id}`}
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                      {inv.invoice_pdf && (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-8 h-8 rounded-lg bg-[#f0faf7] hover:bg-[#16C79A]/15 flex items-center justify-center text-[#16C79A] transition-colors"
                          title="Download PDF"
                          data-testid={`invoice-pdf-${inv.id}`}
                        >
                          <Download size={14} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
